/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isEmpty, isNone, isPresent } from '@ember/utils';
import moment from 'moment-timezone';
import actionInfo, { MESSAGING_TYPES } from 'cio/utils/action-info';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';
import { RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import EnvironmentService from 'cio/services/environment';
import FiltersService, { FilterMessage } from 'cio/services/filters';
import DS from 'ember-data';
import { ActionType } from 'cio/models/action';
import DeliveryModel from 'cio/models/delivery';
import { Task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { Measure } from 'cio/utils/metrics/common';
import { TimeRange } from 'cio/utils/time';
import { RangePreset } from 'cio/utils/date-picker';

const SEARCH_FILTER_QUERY_PARAMS = ['deliveryStatus', 'userActions', 'startTs', 'endTs'];
const FILTER_QUERY_PARAMS = ['campaign', 'action', 'recipient'];

const QUERY_PARAMS_TO_ARGS: Record<string, keyof CioDeliveriesArgs> = {
  campaign: 'campaignId',
  action: 'actionId',
  recipient: 'recipient',
};

const UNSEARCHABLE_DELIVERY_STATES = ['holdout'];

const ACTION_TYPES = MESSAGING_TYPES.map((type) => {
  return {
    label: actionInfo(type).name,
    value: type.replace(/_action$/, ''),
  };
});

export interface ParamsForAction {
  action_id?: string;
  campaign_id?: string;
  delivery_ids?: string[];
  itemLabel?: string;
  newsletter_id?: string;
  override_message_limit?: boolean;
  template_id?: string;
  tracked?: boolean;
  metric?: string;
}
interface CioDeliveriesArgs {
  actionType: string;
  campaignId?: string;
  continuation: number;
  actionId?: string;
  recipient?: string;
  deliveryStatus?: string;
  userActions: string;
  isFilteringByEmailEnabled?: boolean;
  isFilteringEnabled?: boolean;
  fixed?: string;
  deliveries: DeliveryModel[];
  detailRoute?: string;
  fetchDeliveries: Task<unknown, []>;
  fetchMore: Task<unknown, []>;
  drafted?: boolean;
  individualPersonBlankSlate?: boolean;
  searchFiltersByParent?: boolean;
  searchFiltersBySubject?: boolean;

  onUpdate?: (params: ParamsForAction) => unknown;
  onDestroy?: (params: ParamsForAction) => unknown;
  onRetry?: (params: ParamsForAction) => unknown;
  onRedeliver?: (params: ParamsForAction) => unknown;
  onSend?: (params: ParamsForAction) => unknown;
  onClearFilters?: () => unknown;
  onExport?: () => unknown;
  useExportCSVModal?: boolean;
  exportModalMessage?: string;
  unsetExportCSVModalTimestamps?: boolean;
  onActionChange?: (actionId: string | null) => unknown;
  onCampaignChange?: (campaign: string | null) => unknown;
  onRecipientChange?: (recipient: string | null) => unknown;
  resetDeliveryStatus?: () => unknown;
  onDeliveryStatusChange: (deliveryStatus: string | null) => unknown;

  startTs: number | null;
  endTs: number | null;
  onTimeRangeChange: (start: number | null, end: number | null, setExportTime: boolean) => unknown;
  canClearTimeRange?: boolean;
  dateRangeTooltipMessage?: string;
  dateRangePresets?: RangePreset[];
  maxRange?: string;
  hasReadOnlyPermission: boolean;
}

export default class CioDeliveries extends Component<CioDeliveriesArgs> {
  @service environment!: EnvironmentService;
  @service filters!: FiltersService;
  @service store!: DS.Store;

  @tracked lastUpdated = moment().unix();
  @tracked showFilters = false;
  @tracked searchBy!: string | null;
  @tracked timeRange!: TimeRange | undefined;

  cachedActionType: string | null = null;
  actionTypes = ACTION_TYPES;

  get canClearTimeRange() {
    return this.args.canClearTimeRange ?? false;
  }

  get actionId() {
    return this.args.actionId ?? null;
  }

  get actionInfo() {
    let actionType = `${this.args.actionType}_action` as ActionType;
    return actionInfo(actionType);
  }

  get campaignId() {
    return this.args.campaignId ?? null;
  }

  get detailRoute() {
    return this.args.detailRoute ?? 'outbox.deliveries.delivery';
  }

  get drafted() {
    return this.args.drafted ?? false;
  }

  get emptyMessage() {
    let { hasQueryFilters, drafted, timeRange } = this;

    let message = ['There are no'];

    if (drafted) {
      message.push('drafts');
    } else {
      message.push('deliveries');
    }

    if (hasQueryFilters) {
      message.push('matching your filters');
    }

    if (timeRange?.start != null && timeRange?.end != null) {
      message.push(
        `for the selected time range (${timeRange!.start!.format('MMM DD, YYYY')} - ${timeRange!.end!.format(
          'MMM DD, YYYY',
        )})`,
      );
    }

    return message.join(' ');
  }

  get enabledSearchFilters() {
    let fixedItems = this.args.fixed?.split(',') ?? [];
    return SEARCH_FILTER_QUERY_PARAMS.reduce((filtersObj: any, param: any) => {
      filtersObj[param] = !fixedItems.includes(param);
      return filtersObj;
    }, {});
  }

  get errorMessage() {
    return `There was an error trying to load more ${this.drafted ? 'drafts' : 'deliveries'}. Please try again.`;
  }

  get hasEmailQueryFilters() {
    return this.hasQueryFilters && !isEmpty(this.recipient);
  }

  get hasFilters() {
    return (
      this.hasQueryFilters ||
      this.args.actionType != null ||
      this.args.deliveryStatus != null ||
      this.args.startTs != null ||
      this.args.endTs != null ||
      this.campaignId != null
    );
  }

  get hasQueryFilters() {
    let disabledFilters = this.args.fixed?.split(',') ?? [];

    return FILTER_QUERY_PARAMS.any((filterName) => {
      // ignore filters that are disabled
      if (disabledFilters.includes(filterName)) {
        return false;
      }
      let argName = QUERY_PARAMS_TO_ARGS[filterName];

      if (argName == null) {
        return false;
      }

      let value = this.args[argName];

      return !isEmpty(value) && !isNone(value);
    });
  }

  get icon() {
    if (isEmpty(this.args.actionType)) {
      return '';
    }

    return this.actionInfo.icon;
  }

  get individualPersonBlankSlate() {
    return this.args.individualPersonBlankSlate ?? false;
  }

  get isActionTypeNotAll() {
    let { actionType } = this.args;

    return !isEmpty(actionType);
  }

  get isFilteringByEmailEnabled() {
    return this.args.isFilteringByEmailEnabled ?? false;
  }

  get isFilteringEnabled() {
    return this.args.isFilteringEnabled ?? true;
  }

  get recipient() {
    return this.args.recipient ?? null;
  }

  get searchFiltersByParent() {
    return this.args.searchFiltersByParent ?? true;
  }

  get searchFiltersBySubject() {
    return this.args.searchFiltersBySubject ?? true;
  }

  get showDeliveryStatusFilters(): boolean {
    return this.enabledSearchFilters.deliveryStatus;
  }

  get showFilterOptions() {
    return this.args.actionType === 'email' && this.isFilteringEnabled && this.isFilteringByEmailEnabled;
  }

  get showFiltersToggle() {
    let { isActionTypeNotAll, isFilteringEnabled, isFilteringByEmailEnabled } = this;

    // If campaign and action are both fixed, there will be nothing to filter by.
    if (['campaign', 'action'].every((item) => this.args.fixed?.split(',').includes(item))) {
      return false;
    }

    return isFilteringEnabled && isActionTypeNotAll && !(this.args.actionType === 'email' && isFilteringByEmailEnabled);
  }

  get showStateFilters() {
    return this.showDeliveryStatusFilters || this.showUserActionsFilters;
  }

  get showUserActionsFilters(): boolean {
    return this.enabledSearchFilters.userActions;
  }

  get stateFilters() {
    let { actionType } = this.args;
    let actionTypes = this.actionTypes.filter((type) => isPresent(type.value));

    function getFilterOptions(actionTypes: any[], optionsKey: string) {
      return actionTypes
        .flatMap((type) => actionInfo(`${type.value}_action` as ActionType)[optionsKey])
        .filter((option: Measure, i: number, arr: any) => arr.indexOf(option) === i)
        .filter((option: Measure) => isDeliveryMeasureEnabledForChannel(option, actionType as any))
        .filter((option: Measure) => !UNSEARCHABLE_DELIVERY_STATES.includes(option));
    }

    return [
      { groupName: 'Delivery Status', options: getFilterOptions(actionTypes, 'deliveryStates') },
      { groupName: 'User Action Status', options: getFilterOptions(actionTypes, 'deliveryActions') },
    ];
  }

  @action
  onUpdateActionType(_element: HTMLElement, [actionType]: [string]) {
    this.setFilters(actionType);
  }

  @action
  setFilters(actionType: string) {
    if (actionType === this.cachedActionType) {
      return;
    }
    this.cachedActionType = actionType;
    let { isFilteringEnabled, isFilteringByEmailEnabled, isActionTypeNotAll } = this;

    if (!isFilteringEnabled || !isActionTypeNotAll) {
      this.showFilters = false;
      this.searchBy = null;
      return;
    }

    let showFilters = isFilteringByEmailEnabled || this.hasQueryFilters;
    let searchBy = isFilteringEnabled && actionType === 'email' && this.hasEmailQueryFilters ? 'email' : 'filter';

    this.showFilters = showFilters;
    this.searchBy = searchBy;
    return;
  }

  @action
  search(recipient: string) {
    this.args.onRecipientChange?.(recipient);
  }

  @action
  toggleSearchBy(searchBy: string) {
    if (searchBy === 'email') {
      this.args.resetDeliveryStatus?.();
    }

    this.args.onClearFilters?.();
    this.searchBy = searchBy;
  }

  @action
  toggleFilters() {
    this.args.onClearFilters?.();
    this.showFilters = !this.showFilters;
  }

  @action
  onExportAction() {
    this.args.onExport?.();
  }

  @action
  onFilterActionChange(actionId: FilterMessage | null) {
    this.args.onActionChange?.(actionId?.action_id ?? null);
  }

  @action
  onFilterMessageChange(message: { value: string } | null) {
    this.args.onCampaignChange?.(message?.value ?? null);
    this.args.onActionChange?.(null);
  }

  @action
  onDeliveryStatusChange(deliveryStatus: string) {
    this.args.onDeliveryStatusChange(deliveryStatus);
  }

  @action
  onRangeSelect(timeRange?: TimeRange, setExportTime = false) {
    // Logs query will fail if the range is longer than 365 days in seconds.
    let clampedRange: TimeRange | undefined = timeRange;
    if (
      clampedRange?.end != null &&
      clampedRange?.start != null &&
      moment.duration(clampedRange.end.diff(clampedRange.start)).as('years') > 1
    ) {
      clampedRange.start = clampedRange.end.clone().subtract(1, 'year');
    }

    if (!setExportTime) {
      this.timeRange = timeRange;
    }

    if (timeRange == null) {
      this.args.onTimeRangeChange(null, null, setExportTime);
      return;
    }

    if (timeRange?.start != null && timeRange?.end != null) {
      this.args.onTimeRangeChange(timeRange.start.unix(), timeRange.end.unix(), setExportTime);
    }
  }

  constructor(owner: unknown, args: CioDeliveriesArgs) {
    super(owner, args);

    // Set up actionTypes list
    let { fixed } = args;
    let fixedItems = fixed?.split(',') ?? [];

    if (fixedItems.includes('actionType')) {
      this.actionTypes = ACTION_TYPES.filterBy('value', this.args.actionType);
    } else {
      // Add enabled action type dropdown items
      let enabled = ['email', 'webhook'].concat(this.environment.enabledActions);
      if (this.campaignId != null && !isEmpty(this.campaignId)) {
        let campaignModel = this.store.peekRecord('campaign', this.campaignId);
        if (campaignModel !== null) {
          enabled = enabled.filter((typeName) => {
            let restrictedTypes = RESTRICTED_ACTION_TYPES[campaignModel!.type] ?? [];
            return !restrictedTypes.includes(`${typeName}_action` as ActionType);
          });
        }
      }
      let actionTypes = [
        { label: 'All channels', value: '' },
        ...ACTION_TYPES.filter((t) => enabled.includes(t.value)),
      ];
      this.actionTypes = actionTypes;

      let timeRange!: TimeRange;

      if (!isEmpty(this.args.startTs) && !isEmpty(this.args.endTs)) {
        timeRange = {
          start: moment.unix(this.args.startTs!),
          end: moment.unix(this.args.endTs!),
        };
      }
      this.timeRange = timeRange;
    }

    this.setFilters(this.args.actionType);
  }
}
